// The body font. `base.scss` has named Inter since the app was written but nothing ever loaded
// it, so every screen rendered in whatever the OS supplied. The variable face covers 100-900 in
// one file, which is what makes the heading weights restored in `base.scss` real rather than
// synthesised.
import '@fontsource-variable/inter';

import './assets/main.scss';
// The one tooltip's border and arrow, which PrimeVue's tokens can't draw (ENG-9906).
import './assets/tooltip.scss';

import { createApp, type Plugin } from 'vue';
import { createI18n } from 'vue-i18n';
import { FontAwesomeIcon } from './libs/FontAwesomeIcons';
import PrimeVue from 'primevue/config';
import Tooltip from 'primevue/tooltip';
import ToastService from 'primevue/toastservice';
import App from './App.vue';
import router from './router';
import vRipple from './directives/vRipple';
import { createPinia } from 'pinia';
import { VueReCaptcha } from 'vue-recaptcha-v3';
import { AppPreset } from './assets/AppPreset';
import StudySettingsService from './services/studySettingsService';
import StudyUsersService from './services/studyUsersService';
import StudyGroupService from './services/studyGroupService';
import InviteService from './services/inviteService';
import UserService from './services/userService';
import StudyService from './services/studyService';
import StudyLinkService from './services/studyLinkService';
import FormService from './services/formService';
import ExportService from './services/exportService';
import ActivityService from './services/activityService';
import NotificationService from './services/notificationService';
import BlobService from './services/blobService';
import CaseService from './services/caseService';
import EntryService from './services/entryService';
import en from '../locales/en.json';
import es from '../locales/es.json';
import { LoginService, DeepLinkService } from './services';
import AnalyticsService from './services/AnalyticsService';
import SurgeonDashboardService from './services/SurgeonDashboardService';
import VueTelInput from 'vue-tel-input';
import 'vue-tel-input/vue-tel-input.css';
import { LocalStorageUtils, removeBootSkeleton, routeConstants, VueTelInputDefaultConfig } from '@/utils';
import { apiClient } from './services/apiClient';
import {
  buildDiagnosticReport, ErrorSource, initSentry, installGlobalErrorHandlers,
  installHttpErrorInterceptor, normalizeError, renderBootstrapFailure, reportToSentry,
  setErrorTranslator
} from './errors';
import { useErrorStore } from './stores/error';
import { setEntryTranslator } from './stores/entry';
import { setItemRefusalTranslator } from './stores/itemRefusals';

// spdata-api holds the Part 11 PIN now (ENG-9108); a browser that kept one under ENG-9102's stop-gap drops it.
LocalStorageUtils.forgetBrowserPins();

const messages = { en, es };

const i18n = createI18n({
  legacy: false,
  locale: 'en', // Default language
  fallbackLocale: 'en', // Fallback language
  messages
});

/**
 * An expired session, handled once per redirect rather than once per in-flight request.
 *
 * Moved out of `LoadingStateModel.setError` and into the response interceptor: it used to be reachable
 * only from a `catch` block, so the store calls without one — `getStudies()` among them — left the
 * user staring at a spinner instead of being sent to log in.
 */
let redirecting = false;
const onSessionExpired = () => {
  if (redirecting) {
    return;
  }
  redirecting = true;

  if (window.location.href.includes('/investigation/')) {
    /** This is the deep link route. Instead of logging out the user and redirecting them to the /login route,
     * we're simply clearing their expired keys and reloading the page, so it initiates the OAuth login again. */
    LocalStorageUtils.logoutUser();
    window.location.reload();
  } else {
    router.replace({
      name: routeConstants.LOGOUT.name,
      state: { message: 'Logging you out because of the expired session, please login again!' }
    });
  }
};

// Order below is load-bearing.
//
// Sentry and Pinia used to be installed inside the try/catch, with Pinia second to last. That meant a
// throw from any of the 16 service inits reached a `catch` whose `Sentry.captureException` ran before a
// client existed — a silent no-op — and left the page blank. Everything the error path needs is now set
// up before anything that can fail.
const app = createApp(App);

// 1. Earliest possible capture: nothing below this line is unreported.
initSentry(app, router);
// 2. Stores available to the error handlers. `createPinia()` takes no arguments and its install is
//    inert, so hoisting it is safe; stores are still instantiated lazily on first use.
app.use(createPinia());
app.use(router);

/**
 * Tier 0's silhouette is only the truth on the perfusion dashboard, and only until it arrives.
 *
 * `SurgeonDashboard.vue` removes it when it mounts, which is the path that matters — the handoff
 * to tier 1 happens in one frame. These two are for the loads that never reach that mount: a URL
 * that said dashboard but resolved to the login form because the session had expired, and a route
 * chunk that failed to load. Without them the surgeon is left looking at a picture of a page.
 */
const stopBootSkeletonWatch = router.afterEach(to => {
  const isPerfusionRoute =
    to.name === routeConstants.DEFAULT_SURGEON_DASHBOARD.name ||
    to.name === routeConstants.CASE_SURGEON_DASHBOARD.name;
  if (!isPerfusionRoute) {
    removeBootSkeleton();
  }
  // Unregistered whichever way that went. The silhouette exists for the first navigation of a
  // document and cannot come back, so a hook that outlives it is a global running on every route
  // change forever — which is what happened when the first navigation *was* a perfusion route and
  // the only `stopBootSkeletonWatch()` sat on the other branch. On that branch the dashboard's own
  // mount takes the silhouette away.
  stopBootSkeletonWatch();
});
router.onError(() => removeBootSkeleton());
// 3. Before anything can produce a message: `legacy: false` exposes a Composer whose `t` works
//    outside a component, which is what lets `src/errors/`, the entry store's own toasts and
//    tooltip, and the lock, archive and delete refusal toast translate without importing vue-i18n.
app.use(i18n);
setErrorTranslator(i18n.global.t);
setEntryTranslator(i18n.global.t);
setItemRefusalTranslator(i18n.global.t);
// 4. Before any service can fire a request.
installHttpErrorInterceptor(apiClient, { onSessionExpired });

try {
  LoginService.initService();
  DeepLinkService.initService();
  StudySettingsService.initService();
  StudyUsersService.initService('investigation', 'study');
  StudyGroupService.initService('investigation', 'group');
  StudyService.initService('investigation');
  StudyLinkService.initService('investigation');
  InviteService.initService('invite');
  UserService.initService('user');
  FormService.initService('survey');
  ExportService.inviteService('investigation', 'export');
  ActivityService.initService('investigation');
  NotificationService.initService('notifications');
  BlobService.initService('blob');
  CaseService.initService('study', 'investigation', 'case');
  EntryService.initService('study');
  AnalyticsService.initService();
  SurgeonDashboardService.initService();

  app.component('FontAwesomeIcon', FontAwesomeIcon);

  app.use(PrimeVue, {
    theme: {
      preset: AppPreset,
      options: {
        darkModeSelector: 'none'
      }
    }
  });
  app.use(ToastService);
  app.use(VueReCaptcha, {
    siteKey: import.meta.env.VITE_APP_RECAPTCHA_SITE_KEY,
    loaderOptions: {
      useEnterprise: true
    }
  });

  app.directive('ripple', vRipple);
  app.directive('tooltip', Tooltip);

  app.use(VueTelInput as unknown as Plugin, VueTelInputDefaultConfig);

  // Last, so it cannot mask an install-order problem above it.
  installGlobalErrorHandlers(app, router, {
    handle: (error, context) => {
      useErrorStore().handle(error, context);
    }
  });

  app.mount('#app');
} catch (error) {
  // A bootstrap failure used to be a blank page. Report it, then render something with a reference
  // and a way to reach us.
  // Before the failure screen, or it renders underneath the silhouette.
  removeBootSkeleton();
  const normalized = normalizeError(error, { source: ErrorSource.BOOTSTRAP });
  reportToSentry(normalized, buildDiagnosticReport(normalized, {
    release: __APP_RELEASE__,
    commit: __APP_COMMIT__,
    environment: import.meta.env.VITE_APP_ENVIRONMENT || 'staging'
  }));
  renderBootstrapFailure(normalized.reference);
}
